import { Injectable, OnDestroy } from "@angular/core";
import {
  EventTagUnion,
  IDataLayer,
  IProduct,
} from "../interfaces/gtm-interfaces";
import {
  ConsentService,
  ConsentState,
  DEFAULT_CONSENT,
} from "./consent-service";
import {
  BrandViewModel,
  DealerViewModel,
  ModelViewModel,
} from "../domain/client";
import { v4 as uuidv4 } from "uuid";
import { GoogleTagManagerService } from "angular-google-tag-manager";
import {
  DealerSelectionMethod,
  DealerUpdateEventName,
  OrderConfirmationType,
  VehicleSelectionMethod,
  VehicleUpdateEventName,
} from "src/domain/trackingEnums";
import { EcomProductArray } from "src/domain/ecomProductArray";
import { first, take, skip, map, distinctUntilChanged } from "rxjs/operators";
import { BehaviorSubject, Subscription } from "rxjs";
import { ErrorReporterService } from "./error-reporter.service";
import { NonUserError } from "src/errors";
import { GoogleConsentType } from "src/domain/GoogleConsentMode/enums";

type ConsentDTO = {
  [key in GoogleConsentType]: "granted" | "denied";
};

function stateToGtagDTO(consentForm: ConsentState): ConsentDTO {
  return {
    required: consentForm.required ? "granted" : "denied",
    ad_personalization: consentForm.marketing ? "granted" : "denied",
    ad_storage: consentForm.marketing ? "granted" : "denied",
    ad_user_data: consentForm.marketing ? "granted" : "denied",
    analytics_storage: consentForm.performance ? "granted" : "denied",
    functionality_storage: consentForm.functional ? "granted" : "denied",
    personalization_storage: consentForm.functional ? "granted" : "denied",
    security_storage: consentForm.functional ? "granted" : "denied",
  };
}

@Injectable({
  providedIn: "root",
})
export class TrackingService implements OnDestroy {
  hasTrackedServiceTypesOnce = false;
  hasTrackedAddonsOnce = false;
  clientId = uuidv4();

  private subscription = new Subscription();

  private addGtmToDomPromise: Promise<void> | null;

  public gtagConsent: BehaviorSubject<ConsentDTO>;

  constructor(
    private gtmService: GoogleTagManagerService,
    private consentService: ConsentService,
    errorReporter: ErrorReporterService,
  ) {
    window.dataLayer ??= [];
    this.gtagConsent = new BehaviorSubject(stateToGtagDTO(DEFAULT_CONSENT));
    this.subscription.add(
      this.consentService.consentState
        .pipe(
          map((state) => stateToGtagDTO(state)),
          distinctUntilChanged((a, b) =>
            Object.values(GoogleConsentType).every((key) => a[key] === b[key]),
          ),
        )
        .subscribe(this.gtagConsent),
    );
    this.subscription.add(
      this.gtagConsent
        .pipe(take(1))
        .subscribe((value) =>
          this.gtag("consent", "default", { ...value, wait_for_update: 500 }),
        ),
    );
    this.subscription.add(
      this.gtagConsent
        .pipe(skip(1))
        .subscribe((value) => this.gtag("consent", "update", value)),
    );
    this.addGtmToDomPromise = new Promise((resolve) => {
      if (this.subscription.closed) return;
      this.subscription.add(
        this.consentService.gtmEnabled
          .pipe(first((enabled) => enabled))
          .subscribe(() => {
            this.loadGtm()
              .catch((err) => errorReporter.handle(new NonUserError(err)))
              .finally(() => {
                this.addGtmToDomPromise = null;
                resolve();
              });
          }),
      );
    });
  }

  /**
   * Google Tag Manager does black magic when passed an {@link IArguments}
   * object, hence the need for this wrapper function.
   */
  private gtag(...args: any): void;
  private gtag() {
    window.dataLayer.push(arguments);
  }

  private async loadGtm() {
    await this.gtmService.addGtmToDom();
  }

  async pushTag(item: EventTagUnion): Promise<void> {
    if (this.consentService.isTrackingEventAllowed(item)) {
      if (this.addGtmToDomPromise) await this.addGtmToDomPromise;
      console.log("GTM Tag Pushed:\n%o", item);
      return this.gtmService.pushTag(item);
    } else {
      console.log("GTM Tag Rejected by consentService:\n%o", item);
      return Promise.resolve();
    }
  }

  updateDealer(dealer: DealerViewModel | null) {
    const layer: IDataLayer = {
      dealer_name: undefined,
      dealer_id: undefined,
    };
    if (dealer != null) {
      layer.dealer_name = dealer.name;
      layer.dealer_id = dealer.number;
    }
    window.dataLayer.push(layer);
  }

  updateModel(model: ModelViewModel | null) {
    const layer: IDataLayer = {
      model: undefined,
      car_year: undefined,
    };
    if (model != null) {
      layer.model = model.name;
      layer.car_year = model.year;
    }
    window.dataLayer.push(layer);
  }

  updateBrand(brand: BrandViewModel | null) {
    const layer: IDataLayer = {
      brand: undefined,
    };
    if (brand != null) {
      layer.brand = brand.urlShort;
    }
    window.dataLayer.push(layer);
  }

  trackPageChange(url: string, title: string): void {
    if (!this.consentService.gtmEnabled.value) return;
    console.log("Page changed tag pushed");
    this.pushTag({
      event: "page_view",
      page_url: url,
      page_title: title,
    });
  }

  trackUpdateVehicle(
    event: VehicleUpdateEventName,
    usedRegNr: boolean,
    model: string,
    brand: string,
    carYear: string | number,
  ): void {
    this.pushTag({
      event,
      search_method: usedRegNr
        ? VehicleSelectionMethod.REGNR
        : VehicleSelectionMethod.MANUAL,
      model,
      brand,
      car_year: typeof carYear === "number" ? carYear.toFixed(0) : carYear,
    });
  }

  trackUpdateDealer(
    event: DealerUpdateEventName,
    selectedDealer: DealerViewModel,
    selectionMethod: DealerSelectionMethod,
  ): void {
    if (
      !selectedDealer ||
      selectedDealer.name == null ||
      selectedDealer.number == null
    )
      return; // DONT TRACK IF NON SELECTED
    this.pushTag({
      event,
      search_method: selectionMethod,
      dealer_name: selectedDealer.name,
      dealer_id: selectedDealer.number.toString(),
    });
  }

  trackOrderSubmit() {
    this.pushTag({
      event: "order_submit",
      confirmation_type: OrderConfirmationType.MAIL,
    });
  }

  // add to cart på nästa
  trackAddToCartEcom(addedProducts: readonly IProduct[]): void {
    this.pushTag({
      event: "add_to_cart",
      ecommerce: {
        currency: "SEK",
        value: EcomProductArray.calculateTotalFromProductArray(addedProducts),
        items: addedProducts,
      },
    });
  }

  trackRemoveFromCartEcom(removedProducts: readonly IProduct[]): void {
    this.pushTag({
      event: "remove_from_cart",
      ecommerce: {
        currency: "SEK",
        value: EcomProductArray.calculateTotalFromProductArray(removedProducts),
        items: removedProducts,
      },
    });
  }

  trackPurchaseCompleteEcom(
    products: readonly IProduct[],
    orderNumber: number,
    couponCode?: string,
    contactRequest?: boolean,
  ): void {
    this.pushTag({
      event: "purchase",
      ecommerce: {
        transaction_id:
          typeof orderNumber === "number" ? orderNumber.toFixed() : orderNumber,
        value: EcomProductArray.calculateTotalFromProductArray(products),
        currency: "SEK",
        coupon: couponCode,
        items: products,
      },
      ...(contactRequest != null
        ? { contact_request: contactRequest ? "Yes" : "No" }
        : null),
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
